import {
  ExperienceTemplateStatus,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';

export type ActorEditRules = {
  // Every field of an existing actor is read-only.
  disableEditing: boolean;
  // The name is read-only: the key is derived from it, and live instances
  // reference the key.
  disableRename: boolean;
  canRemove: boolean;
  // The edit can reach live instances, so saving says which ones it's for.
  propagates: boolean;
  // Ask whether the edit reaches live instances or only new ones. When it
  // propagates without asking, it's saved for all of them.
  asksPropagationScope: boolean;
  // Saving the actor as this type is refused.
  isBlockedTypeChange: (nextType: SupportingActorType) => boolean;
};

/**
 * What the supporting actor dialog lets the current user do. On a launched
 * template an existing actor can be edited only by the Dado team and by those
 * who can edit the template (account admins, and experience managers given
 * edit rights on it); they can't rename it or hard remove it — archiving is
 * how they retire it. Adding a new actor is never restricted.
 */
export const getActorEditRules = ({
  status,
  existingActorType,
  isDadoEmployee,
  canEditTemplate
}: {
  status: ExperienceTemplateStatus;
  // null when adding a new actor.
  existingActorType: SupportingActorType | null;
  isDadoEmployee: boolean;
  canEditTemplate: boolean;
}): ActorEditRules => {
  const isUnlaunched =
    status === ExperienceTemplateStatus.ROUGH_DRAFT ||
    status === ExperienceTemplateStatus.DRAFT;
  const isLaunchedActor = existingActorType !== null && !isUnlaunched;
  const disableEditing = isLaunchedActor && !isDadoEmployee && !canEditTemplate;
  // Only a template with live instances has anything to propagate to: drafts
  // have none, and archived templates don't propagate.
  const propagates =
    existingActorType !== null &&
    !disableEditing &&
    (status === ExperienceTemplateStatus.ACTIVE ||
      status === ExperienceTemplateStatus.INACTIVE);
  return {
    disableEditing,
    disableRename: isLaunchedActor && !isDadoEmployee,
    canRemove: isUnlaunched || isDadoEmployee,
    propagates,
    // A Slack channel's edit always reaches live instances too.
    asksPropagationScope:
      propagates && existingActorType !== SupportingActorType.CHANNEL,
    // Live instances can't turn a person into a Slack channel.
    isBlockedTypeChange: (nextType) =>
      isLaunchedActor &&
      existingActorType !== SupportingActorType.CHANNEL &&
      nextType === SupportingActorType.CHANNEL
  };
};
